import Skeleton from 'react-loading-skeleton';
import { Price } from '../__simple/_price';
import BanksPanel from './BanksPanel';

// Renders a labelled row of clickable circular color swatches.
// Each swatch shows the color preview (image, base64, or fallback fill),
// highlights when active, and calls `onPick(ColorID)` on click.
function ColorSwatchGroup({ label, colors, activeId, onPick, config }) {
    if (!colors || colors.length === 0) return null;

    const previewFor = (color) => {
        if (color?.ColorImage) {
            return `url(${(config?.FilePathURL + color.ColorImage).replaceAll('\\', '/')})`;
        }
        if (color?.ColorBase64) {
            return `url(data:image/png;base64,${color.ColorBase64})`;
        }
        return 'none';
    };

    const fallbackBg = (id = '') => {
        if (id.includes('089')) return '#f5f5f5';
        if (id.includes('S44')) return '#808080';
        return '#ecefef';
    };

    return (
        <div style={{ marginBottom: 20, flex: '0 0 auto' }}>
            <h3
                style={{
                    fontSize: 14,
                    fontWeight: 700,
                    marginBottom: 10,
                    color: 'var(--t1)',
                }}
            >
                {label}
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                {colors.map((color, index) => {
                    const isActive = activeId === color.ColorID;
                    return (
                        <button
                            key={color.ColorID || index}
                            type="button"
                            title={color.ColorDescription}
                            onClick={() => onPick && onPick(color.ColorID)}
                            style={{
                                width: 42,
                                height: 42,
                                borderRadius: '50%',
                                border: isActive
                                    ? '2px solid var(--g)'
                                    : '2px solid transparent',
                                padding: 2,
                                background: 'transparent',
                                transition: 'all .25s ease',
                                cursor: 'pointer',
                                outline: 'none',
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <div
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    backgroundColor: fallbackBg(color.ColorID),
                                    backgroundImage: previewFor(color),
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {!color.ColorImage && !color.ColorBase64 && (
                                    <span
                                        style={{
                                            fontSize: 9,
                                            fontWeight: 'bold',
                                            color: '#555',
                                        }}
                                    >
                                        {color.ColorID}
                                    </span>
                                )}
                                {isActive && (
                                    <div
                                        style={{
                                            width: 18,
                                            height: 18,
                                            background: 'rgba(24, 169, 78, 0.9)',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                        }}
                                    >
                                        <img
                                            src="/assets/icons/check.svg"
                                            alt=""
                                            style={{
                                                width: 10,
                                                height: 10,
                                                filter: 'brightness(0) invert(1)',
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default function DesktopSidebar({
    isLoading,
    vehicle,
    isToyota,
    isLexus,
    trans,
    selectedColor,
    minFinance,
    finance,
    financeSettings,
    lang,
    Cart,
    setCartData,
    history,
    setShowFinalPopupMessage,
    setShareOpened,
    isFavoriteItem,
    changeFavoriteItemState,
    banks,
    setFinanceSettings,
    setSelectedFinancePeriod,
    reCountFinanceSettings,
    config,
    OUTcolors,
    INcolors,
    activeOutColor,
    setActiveOutColor,
    activeINColor,
    setActiveINColor,
    setSelectedColor
}) {
    return (
        <div className="vh-info">
            {isLoading ? (
                <div style={{ padding: '0 10px' }}>
                    <Skeleton baseColor="rgba(0,0,0,0.05)" width="30%" height="20px" style={{ marginBottom: '10px' }} />
                    <Skeleton baseColor="rgba(0,0,0,0.05)" width="80%" height="40px" style={{ marginBottom: '20px' }} />
                    <Skeleton baseColor="rgba(0,0,0,0.05)" width="100%" height="120px" borderRadius="1rem" style={{ marginBottom: '24px' }} />
                    <Skeleton baseColor="rgba(0,0,0,0.05)" width="100%" height="180px" borderRadius="1rem" />
                </div>
            ) : vehicle ? (
                <>
                    <div className="vh-brand">{vehicle?.brand}</div>
                    <h1 className="vh-name rv vis d1">{vehicle?.name} <span style={{ color: "var(--g)" }}>{vehicle?.year}</span></h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span className="vh-tag rv vis d2" style={{ margin: 0 }}>{lang === 'ar' ? vehicle?.carCategoriesAr : vehicle?.carCategories}</span>
                    </div>
                    {/* Color swatches — clickable circular previews that swap the
                        car image. Same UX as vehicles/details/overview.jsx. */}
                    <ColorSwatchGroup
                        label={trans('new:exterior_color')}
                        colors={OUTcolors}
                        activeId={activeOutColor}
                        config={config}
                        onPick={(id) => {
                            if (id !== activeOutColor) {
                                setSelectedColor && setSelectedColor([]);
                                setActiveOutColor && setActiveOutColor(id);
                            }
                        }}
                    />
                    <ColorSwatchGroup
                        label={trans('new:interior_color')}
                        colors={INcolors}
                        activeId={activeINColor}
                        config={config}
                        onPick={(id) => {
                            if (id !== activeINColor) {
                                setSelectedColor && setSelectedColor([]);
                                setActiveINColor && setActiveINColor(id);
                            }
                        }}
                    />

                    {selectedColor?.buyOnline ? (
                        <div className="vh-price-box rv vis d3">
                            <div className="vh-price">
                                {selectedColor?.NetSalePrice && (
                                    <Price price={parseFloat(selectedColor?.NetSalePrice)} displayCurrencyLiter={false} />
                                )}
                                {' '}<small>{trans('new:cash_price')}</small>
                            </div>
                            {minFinance?.price && (
                                <div className="vh-monthly">
                                    {trans('new:installment_starts')} <strong>~{minFinance?.price.toFixed(2)} / {trans('new:monthly')}</strong>
                                </div>
                            )}
                            {finance?.price && (financeSettings?.DescriptionAr || financeSettings?.DescriptionEn) && (
                                <div className="vh-monthly" onClick={() => setShowFinalPopupMessage(true)} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                                    {lang === 'ar' ? financeSettings.DescriptionAr : financeSettings.DescriptionEn}
                                    {' (~'}{finance?.price.toFixed(2)}{' '}{trans('new:monthly')}{')'}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="vh-price-box rv vis d3" style={{ textAlign: 'center' }}>
                            <span style={{ color: 'red', fontWeight: 'bold' }}>{trans('new:not_available')}</span>
                            <div style={{ marginTop: '10px', fontSize: '13px', color: 'var(--co-info, #3b82f6)', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); alert(trans('new:Notification_will_be_sent_when_available')); }}>
                                {trans('new:alert_when_available')}
                            </div>
                        </div>
                    )}

                    {selectedColor?.buyOnline && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {!isLoading && banks?.length > 0 && (
                                <BanksPanel
                                    banks={banks}
                                    lang={lang}
                                    financeSettings={financeSettings}
                                    setFinanceSettings={setFinanceSettings}
                                    setSelectedFinancePeriod={setSelectedFinancePeriod}
                                    reCountFinanceSettings={reCountFinanceSettings}
                                    trans={trans}
                                    config={config}
                                />
                            )}
                            <div className="vh-btns rv vis d4">
                                <button className="vh-cta green" onClick={() => {
                                    try {
                                        if (vehicle) {
                                            history.push({
                                                pathname: `/car/checkout/${selectedColor?.SN}/${selectedColor?.ProductTarget}`,
                                                search: '',
                                            });
                                        }
                                    } catch (e) { }
                                }}>
                                    {trans('new:book_now')}
                                </button>
                                <button className="vh-cta outline" onClick={() => setShareOpened(v => !v)}>
                                    <img src="/assets/icons/share.svg" alt="share" style={{ width: '18px', height: '18px', objectFit: 'contain' }} />
                                    {trans('new:share')}
                                </button>
                                <button className="vh-cta ghost" onClick={() => { if (Cart) setCartData(Cart); }}>
                                    <img src="/assets/icons/cart.svg" alt="cart" style={{ width: '18px', height: '18px', objectFit: 'contain' }} />
                                    {trans('new:add_cart')}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="my-10">
                    <h4 className="text-gray text-20 py-2">{trans('new:no_data')}</h4>
                </div>
            )}
        </div>
    );
}